import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ChartOptions, ChartType, ChartDataset } from 'chart.js';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { BusinessesService } from 'src/app/services/businesses.service';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';
import flatpickr from 'flatpickr';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { AnalyticsHelpersService } from './analytics-helpers.service';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit, OnDestroy {

  @Input() businessId!: string;
  last12MonthsAnalyticsData: any = null;
  last7daysAnalyticsData: any = null;
  selectedTimeframeBar: string = 'last12Months';
  selectedTimeframeLine: string = 'last12Months';
  notificationsOpenedAnalytics: any;
  notificationOpened: string = '';
  totalUsers: string = '';
  totalBarChartEvents: number = 0;
  totalLineChartEvents: number = 0;
  totalBarChartUsers: number = 0;
  totalLineChartUsers: number = 0;
  // Add new properties for custom date range
  customStartDate: string = '';
  customEndDate: string = '';
  // Update variable names to be more specific
  private barChartFlatpickrInstance: FlatpickrInstance | null = null;
  private lineChartFlatpickrInstance: FlatpickrInstance | null = null;
  // Add separate custom date range properties for line chart
  barChartCustomStartDate: string = '';
  barChartCustomEndDate: string = '';
  lineChartCustomStartDate: string = '';
  lineChartCustomEndDate: string = '';
  // Add new properties to store formatted date strings
  barChartCustomDateRangeLabel: string = 'Custom';
  lineChartCustomDateRangeLabel: string = 'Custom';
  // Add new properties to store previous selections
  private previousTimeframeBar: string = 'last12Months';
  private previousTimeframeLine: string = 'last12Months';
  // Playground filtering state
  managedPlaygroundIds: { id: string; name: string }[] = [];
  selectedPlaygroundIds: Set<string> = new Set<string>();
  tempSelectedPlaygroundIds: Set<string> = new Set<string>();
  allSelectedPlaygrounds: boolean = true; // persisted state
  tempAllSelectedPlaygrounds: boolean = true; // UI temp state while dropdown open
  isPlaygroundFilterOpen: boolean = false;

  constructor(
    private afAuth: AngularFireAuth,
    private http: HttpClient,
    private analytics: AnalyticsService,
    private businessesService: BusinessesService,
    public loadingSpinnerService: LoadingSpinnerService,
    private helpers: AnalyticsHelpersService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.loadManagedPlaygroundIds();
    this.fetchInitialAnalyticsData();
    // Initialize with last 7 days as default custom range for both charts
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);

    this.barChartCustomStartDate = this.helpers.formatDate(start);
    this.barChartCustomEndDate = this.helpers.formatDate(end);
    this.lineChartCustomStartDate = this.helpers.formatDate(start);
    this.lineChartCustomEndDate = this.helpers.formatDate(end);

    // Initialize flatpickr after view is ready
    setTimeout(() => this.initializeDatePickers(), 0);
  }

  isAnalyticsDataValid(key: string): boolean {
    const storedData = localStorage.getItem(key);
    if (!storedData) return false;

    const dataObj = JSON.parse(storedData);
    const dataTime = dataObj.timestamp;
    const currentTime = new Date().getTime();
    // Check if stored data is older than 24 hours
    return currentTime - dataTime < 86400000;
  }
  // Centralized localStorage get/set
  getStoredAnalyticsData(key: string): any {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData).data : null;
  }

  storeAnalyticsData(timeRange: string, eventName: string, processedData: any): void {
    const dataToStore = {
      timestamp: new Date().getTime(),
      data: processedData,
    };
    const localStorageKey = this.getAnalyticsLocalStorageKey(timeRange, eventName);
    localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
  }

  async getUniqueUsersCount(timeRange: string, eventName: string): Promise<number> {
    // Fetch unique user count without date dimension to avoid double-counting
    const localStorageKey = this.getAnalyticsLocalStorageKey(`${timeRange}_uniqueUsers`, eventName);
    
    let userData: any;
    if (this.isAnalyticsDataValid(localStorageKey)) {
      userData = this.getStoredAnalyticsData(localStorageKey);
      return userData?.uniqueUsers || 0;
    }

    const encryptedAccessToken = localStorage.getItem('EAT');
    let decryptedAccessToken: any;
    if (encryptedAccessToken) {
      const decryptedBytes = CryptoJS.AES.decrypt(encryptedAccessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N');
      decryptedAccessToken = decryptedBytes.toString(CryptoJS.enc.Utf8);
    } else {
      return 0;
    }

    try {
      const body = this.buildUniqueUsersRequestBody(timeRange, eventName);
      const data = await this.analytics.getAnalyticsData(decryptedAccessToken, body);
      
      // Extract unique user count from response
      const uniqueUsers = data?.rows?.[0]?.metricValues?.[0]?.value 
        ? parseInt(data.rows[0].metricValues[0].value, 10) 
        : 0;
      
      // Cache the result
      const dataToStore = {
        timestamp: new Date().getTime(),
        data: { uniqueUsers },
      };
      localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
      
      return uniqueUsers;
    } catch (error: any) {
      console.error('Error fetching unique users count:', error);
      return 0;
    }
  }

  buildUniqueUsersRequestBody(timeRange: string, eventName: string): any {
    let startDate: string = "";
    let endDate: string = "";

    const selectedIds = this.getSelectedPlaygroundIdsForQuery();
    const businessIdFilter = this.buildBusinessIdFilterExpression(selectedIds);

    // Handle custom date range
    if (timeRange === 'custom' && this.customStartDate && this.customEndDate) {
      startDate = this.customStartDate;
      endDate = this.customEndDate;
    } else if (timeRange === "last7days") {
      startDate = "7daysAgo";
      endDate = "today";
    } else if (timeRange === "last30days") {
      startDate = "30daysAgo";
      endDate = "today";
    } else if (timeRange === "last12Months") {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
      const daysLeft = Math.ceil(
        (lastDayOfMonth.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      const last12MonthsStartDaysNumber = 365 - daysLeft;
      startDate = last12MonthsStartDaysNumber.toString() + "daysAgo";
      endDate = "yesterday";
    }

    // Return request body with only eventName dimension (no date) to get unique users
    return {
      dimensions: [{ name: "eventName" }],
      metrics: [{ name: "totalUsers" }],
      dimensionFilter: {
        and_group: {
          expressions: [
            {
              filter: {
                fieldName: "eventName",
                stringFilter: {
                  matchType: "EXACT",
                  value: eventName
                }
              }
            },
            businessIdFilter
          ]
        }
      },
      dateRanges: [{ startDate, endDate }]
    };
  }

  async getAnalyticsData(timeRange: string, eventName: string): Promise<any> {
    const localStorageKey = this.getAnalyticsLocalStorageKey(timeRange, eventName);
    if (this.isAnalyticsDataValid(localStorageKey)) {
      return this.getStoredAnalyticsData(localStorageKey);
    }

    return new Promise(async (resolve, reject) => {
      const encryptedAccessToken = localStorage.getItem('EAT');
      let decryptedAccessToken: any;
      if (encryptedAccessToken) {
        const decryptedBytes = CryptoJS.AES.decrypt(encryptedAccessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N');
        decryptedAccessToken = decryptedBytes.toString(CryptoJS.enc.Utf8);
      } else {
        this.getNewAccessToken(timeRange, eventName);
      }
      try {
        const body = this.buildAnalyticsRequestBody(timeRange, eventName);
        const data = await this.analytics.getAnalyticsData(decryptedAccessToken, body);

        resolve(data);
      } catch (error: any) {
        if (error.status === 401) {
          this.getNewAccessToken(timeRange, eventName);
        }
        reject(error);
      }
    });
  }

  async getNewAccessToken(timeRange: string, eventName: string) {
    try {
      localStorage.removeItem('EAT');
      this.afAuth.idToken.subscribe(async (userAccessToken) => {
        if (userAccessToken) {
          const headers = new HttpHeaders({
            Authorization: `Bearer ${userAccessToken}`,
            'Content-Type': 'application/json'
          });

          const response: any = await this.http.post(environment.accessTokenUrl, {}, { headers }).toPromise();

          const encryptedAccessToken = CryptoJS.AES.encrypt(response.accessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N').toString();
          localStorage.setItem('EAT', encryptedAccessToken);

          this.getAnalyticsData(timeRange, eventName);
        } else {
          console.error('User not authenticated');
        }
      });
    } catch (error) {
      console.error('Error fetching new access token:', error);
    }
  }

  buildAnalyticsRequestBody(timeRange: string, eventName: string): any {
    let startDate: string = "";
    let endDate: string = "";

    const selectedIds = this.getSelectedPlaygroundIdsForQuery();
    const businessIdFilter = this.buildBusinessIdFilterExpression(selectedIds);

    // Add custom date range handling
    if (timeRange === 'custom' && this.customStartDate && this.customEndDate) {
      startDate = this.customStartDate;
      endDate = this.customEndDate;
      return {
        dimensions: [{ name: "eventName" }, { name: "date" }],
        metrics: [{ name: "eventCount" }, { name: "totalUsers" }],
        dimensionFilter: {
          and_group: {
            expressions: [
              {
                filter: {
                  fieldName: "eventName",
                  stringFilter: {
                    matchType: "EXACT",
                    value: eventName
                  }
                }
              },
              businessIdFilter
            ]
          }
        },
        dateRanges: [{ startDate, endDate }]
      };
    }

    // Logic for when timeRange is not needed for specific events
    if (eventName === "notificationOpened") {
      startDate = '2024-01-01';
      endDate = 'yesterday';
      return {
        dimensions: [{ name: "eventName" }],
        metrics: [{ name: "eventCount" }],  // Only eventCount for notifications
        dimensionFilter: {
          and_group: {
            expressions: [
              {
                filter: {
                  fieldName: "eventName",
                  stringFilter: {
                    matchType: "EXACT",
                    value: eventName
                  }
                }
              },
              businessIdFilter
            ]
          }
        },
        dateRanges: [{ startDate, endDate }]
      };
    }

    // Logic for totalUsers - get all-time total users
    // Remove date dimension to get true unique user count across entire date range
    if (timeRange === 'totalUsers') {
      startDate = '2024-01-01';
      endDate = 'yesterday';
      return {
        dimensions: [{ name: "eventName" }],
        metrics: [{ name: "totalUsers" }],  // Only totalUsers metric, no eventCount needed
        dimensionFilter: {
          and_group: {
            expressions: [
              {
                filter: {
                  fieldName: "eventName",
                  stringFilter: {
                    matchType: "EXACT",
                    value: eventName
                  }
                }
              },
              businessIdFilter
            ]
          }
        },
        dateRanges: [{ startDate, endDate }]
      };
    }

    // Logic for when timeRange is used
    const timeDimension = timeRange === "last12Months" ? "month" : "day";

    if (timeRange === "last7days") {
      startDate = "7daysAgo";
      endDate = "today";
    } else if (timeRange === "last30days") {
      startDate = "30daysAgo";
      endDate = "today";
    } else if (timeRange === "last12Months") {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();

      const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
      const daysLeft = Math.ceil(
        (lastDayOfMonth.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );

      const last12MonthsStartDaysNumber = 365 - daysLeft;
      startDate = last12MonthsStartDaysNumber.toString() + "daysAgo";
      endDate = "yesterday";
    }

    // Return the response with timeRange
    return {
      dimensions: [{ name: "eventName" }, { name: timeDimension }],
      metrics: [{ name: "eventCount" }, { name: "totalUsers" }],
      dimensionFilter: {
        and_group: {
          expressions: [
            {
              filter: {
                fieldName: "eventName",
                stringFilter: {
                  matchType: "EXACT",
                  value: eventName
                }
              }
            },
            businessIdFilter
          ]
        }
      },
      dateRanges: [{ startDate, endDate }]
    };
  }

  processAnalyticsData(timeRange: string, analyticsData: any, eventName: string): any {
    // If data is already processed and in the expected shape from localStorage, return it directly
    if (analyticsData && typeof analyticsData === 'object' && 'chartData' in analyticsData) {
      const chartData = analyticsData.chartData || {};
      const totalUsers = typeof analyticsData.totalUsers === 'number' ? analyticsData.totalUsers : 0;
      return { chartData, totalUsers };
    }

    let data: { [key: string]: number } = {};
    let totalUsers = 0;

    if (Array.isArray(analyticsData.rows)) {
      analyticsData.rows.forEach((row: { dimensionValues: { value: any; }[]; metricValues: { value: string; }[]; }) => {
        const dimensionValue = row.dimensionValues[1]?.value || null;
        
        // For totalUsers timeRange, we only have totalUsers metric at index 0
        if (timeRange === 'totalUsers') {
          totalUsers = parseInt(row.metricValues[0].value, 10);  // totalUsers is at index 0
          return; // Skip the rest of the processing for this row
        }
        
        // For notificationOpened (timeRange '0'), we only have eventCount at index 0
        if (timeRange === '0') {
          const eventCount = parseInt(row.metricValues[0].value, 10);
          const label = eventName;
          data[label] = (data[label] || 0) + eventCount;
          totalUsers = 0;  // These queries don't request totalUsers metric
          return; // Skip the rest
        }
        
        // For all other timeRanges, we have eventCount at [0] and totalUsers at [1]
        const eventCount = parseInt(row.metricValues[0].value, 10);
        const rowTotalUsers = parseInt(row.metricValues[1].value, 10);
        totalUsers += rowTotalUsers;  // Sum for other timeRanges (though this is still per-date)

        // Add custom date range handling
        if (timeRange === 'custom') {
          // Convert YYYYMMDD to YYYY-MM-DD
          const dateStr = dimensionValue;
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          const label = `${year}-${month}-${day}`;
          data[label] = (data[label] || 0) + eventCount;
        } else if (timeRange === 'last12Months') {
          const label = this.helpers.getMonthName(dimensionValue);
          data[label] = (data[label] || 0) + eventCount;
        } else if (timeRange === 'last7days') {
          const dayOfMonth = parseInt(dimensionValue, 10);
          const today = new Date();
          let targetDate;

          if (dayOfMonth > today.getDate()) {
            targetDate = new Date(today.getFullYear(), today.getMonth() - 1, dayOfMonth);
          } else {
            targetDate = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);
          }

          const label = this.helpers.getDayOfWeekLabel(targetDate);
          data[label] = (data[label] || 0) + eventCount;
        } else if (timeRange === 'last30days') {
          const dayOfMonth = parseInt(dimensionValue, 10);
          const today = new Date();
          let targetDate;

          if (dayOfMonth > today.getDate()) {
            targetDate = new Date(today.getFullYear(), today.getMonth() - 1, dayOfMonth);
          } else {
            targetDate = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);
          }

          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

          if (targetDate >= thirtyDaysAgo && targetDate <= today) {
            const label = targetDate.toISOString().split('T')[0];
            data[label] = (data[label] || 0) + eventCount;
          }
        }
      });

      // Add custom date range handling for data processing
      if (timeRange === 'custom') {
        const customDateLabels = this.helpers.getCustomDateRangeLabels(this.customStartDate, this.customEndDate);
        const formattedData: { [key: string]: number } = {};
        customDateLabels.forEach(dateStr => {
          formattedData[dateStr] = data[dateStr] || 0;
        });
        data = formattedData;

        return { chartData: data, totalUsers: totalUsers };
      }

      // Fill missing labels and store data for all timeframes
      if (timeRange === 'last12Months') {
        const monthLabels = this.helpers.getMonthsLabels();
        monthLabels.forEach(month => {
          if (!(month in data)) {
            data[month] = 0;
          }
        });
        this.storeAnalyticsData(timeRange, eventName, { chartData: data, totalUsers: totalUsers });
      } else if (timeRange === 'last7days') {
        const weekLabels = this.helpers.getLast7DaysLabels();
        weekLabels.forEach(day => {
          if (!(day in data)) {
            data[day] = 0;
          }
        });
        this.storeAnalyticsData(timeRange, eventName, { chartData: data, totalUsers: totalUsers });
      } else if (timeRange === 'last30days') {
        const thirtyDayLabels = this.helpers.getLast30DaysLabels();
        const formattedData: { [key: string]: number } = {};
        thirtyDayLabels.forEach(dateStr => {
          formattedData[dateStr] = data[dateStr] || 0;
        });
        data = formattedData;
        this.storeAnalyticsData(timeRange, eventName, { chartData: data, totalUsers: totalUsers });
      } else {
        this.storeAnalyticsData(timeRange, eventName, { chartData: data, totalUsers: totalUsers });
      }

      const processedData = {
        chartData: data,
        totalUsers: totalUsers
      };
      return processedData;
    }
    return { chartData: data, totalUsers: 0 };
  }

  async fetchInitialAnalyticsData(): Promise<void> {
    try {

      this.updateBarChart();
      await this.updateLineChart();

      this.notificationsOpenedAnalytics = await this.getAnalyticsData('0', 'notificationOpened')
      const processedData = this.processAnalyticsData("0", this.notificationsOpenedAnalytics, "notificationOpened");

      this.notificationOpened = processedData.chartData.notificationOpened > 0 ? processedData.chartData.notificationOpened : "0";

      // Fetch totalUsers with hardcoded date range (all-time)
      const totalUsersAnalytics = await this.getAnalyticsData('totalUsers', 'storyOpened');
      const totalUsersProcessedData = this.processAnalyticsData("totalUsers", totalUsersAnalytics, "storyOpened");
      this.totalUsers = totalUsersProcessedData.totalUsers > 0 ? totalUsersProcessedData.totalUsers.toString() : "0";

    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  }

  async updateBarChart(timeframe?: string): Promise<void> {
    // Store previous selection before updating
    if (this.selectedTimeframeBar !== 'custom1') {
      this.previousTimeframeBar = this.selectedTimeframeBar;
    }

    this.selectedTimeframeBar = timeframe || this.selectedTimeframeBar;
    if (this.selectedTimeframeBar === 'custom1') {
      return;
    }
    if (this.selectedTimeframeBar === 'custom') {
      const analyticsData = await this.getAnalyticsData('custom', "businessLinkOpened");
      const processedData = this.processAnalyticsData('custom', analyticsData, "businessLinkOpened");

      const customLabels = this.helpers.getCustomDateRangeLabels(this.customStartDate, this.customEndDate);
      this.barChartLabels = customLabels.map(date => {
        const [year, month, day] = date.split('-');
        return `${day} ${this.helpers.getMonthName(month)}`;
      });

      this.barChartData[0].data = customLabels.map(label => processedData.chartData[label] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalBarChartUsers = await this.getUniqueUsersCount('custom', "businessLinkOpened");
      this.totalBarChartEvents = this.barChartData[0].data.reduce((sum, value) => sum + (value || 0), 0);

      this.barChartData = [...this.barChartData];
      return;
    }

    let processedData: any;

    // Get the appropriate labels and check stored data
    if (this.selectedTimeframeBar === 'last30days') {
      const fullDateLabels = this.helpers.getLast30DaysLabels();
      this.barChartLabels = this.helpers.getDisplayLabelsFor30Days();

      const localStorageKey = this.getAnalyticsLocalStorageKey(this.selectedTimeframeBar, "businessLinkOpened");

      if (this.isAnalyticsDataValid(localStorageKey)) {
        processedData = this.getStoredAnalyticsData(localStorageKey);
      } else {
        const analyticsData = await this.getAnalyticsData(this.selectedTimeframeBar, "businessLinkOpened");
        processedData = this.processAnalyticsData(this.selectedTimeframeBar, analyticsData, "businessLinkOpened");
      }

      // Map the data using full dates as keys
      this.barChartData[0].data = fullDateLabels.map(dateStr => processedData.chartData[dateStr] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalBarChartUsers = await this.getUniqueUsersCount(this.selectedTimeframeBar, "businessLinkOpened");
    } else {
      this.barChartLabels = this.selectedTimeframeBar === 'last12Months'
        ? this.helpers.getMonthsLabels()
        : this.helpers.getLast7DaysLabels();

      const localStorageKey = this.getAnalyticsLocalStorageKey(this.selectedTimeframeBar, "businessLinkOpened");

      if (this.isAnalyticsDataValid(localStorageKey)) {
        processedData = this.getStoredAnalyticsData(localStorageKey);
      } else {
        const analyticsData = await this.getAnalyticsData(this.selectedTimeframeBar, "businessLinkOpened");
        processedData = this.processAnalyticsData(this.selectedTimeframeBar, analyticsData, "businessLinkOpened");
      }

      this.barChartData[0].data = this.barChartLabels.map(label => processedData.chartData[label] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalBarChartUsers = await this.getUniqueUsersCount(this.selectedTimeframeBar, "businessLinkOpened");
    }

    // Calculate total events
    this.totalBarChartEvents = this.barChartData[0].data.reduce((sum, value) => sum + (value || 0), 0);

    this.barChartData = [...this.barChartData];
  }

  async updateLineChart(timeframe?: string): Promise<void> {
    // Store previous selection before updating
    if (this.selectedTimeframeLine !== 'custom1') {
      this.previousTimeframeLine = this.selectedTimeframeLine;
    }

    this.selectedTimeframeLine = timeframe || this.selectedTimeframeLine;
    if (this.selectedTimeframeLine === 'custom1') {
      return;
    }
    if (this.selectedTimeframeLine === 'custom') {
      const analyticsData = await this.getAnalyticsData('custom', "storyOpened");
      const processedData = this.processAnalyticsData('custom', analyticsData, "storyOpened");

      const customLabels = this.helpers.getCustomDateRangeLabels(this.customStartDate, this.customEndDate);
      this.lineChartLabels = customLabels.map(date => {
        const [year, month, day] = date.split('-');
        return `${day} ${this.helpers.getMonthName(month)}`;
      });

      this.lineChartData[0].data = customLabels.map(label => processedData.chartData[label] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalLineChartUsers = await this.getUniqueUsersCount('custom', "storyOpened");
      this.totalLineChartEvents = this.lineChartData[0].data.reduce((sum: number, value) => {
        const numValue = typeof value === 'number' ? value : 0;
        return sum + numValue;
      }, 0);

      this.lineChartData = [...this.lineChartData];
      return;
    }

    let processedData: any;

    if (this.selectedTimeframeLine === 'last30days') {
      const fullDateLabels = this.helpers.getLast30DaysLabels();
      this.lineChartLabels = this.helpers.getDisplayLabelsFor30Days();

      const localStorageKey = this.getAnalyticsLocalStorageKey(this.selectedTimeframeLine, "storyOpened");

      if (this.isAnalyticsDataValid(localStorageKey)) {
        processedData = this.getStoredAnalyticsData(localStorageKey);
      } else {
        const analyticsData = await this.getAnalyticsData(this.selectedTimeframeLine, "storyOpened");
        processedData = this.processAnalyticsData(this.selectedTimeframeLine, analyticsData, "storyOpened");
      }

      // Map the data using full dates as keys
      this.lineChartData[0].data = fullDateLabels.map(dateStr => processedData.chartData[dateStr] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalLineChartUsers = await this.getUniqueUsersCount(this.selectedTimeframeLine, "storyOpened");
    } else {
      this.lineChartLabels = this.selectedTimeframeLine === 'last12Months'
        ? this.helpers.getMonthsLabels()
        : this.helpers.getLast7DaysLabels();

      const localStorageKey = this.getAnalyticsLocalStorageKey(this.selectedTimeframeLine, "storyOpened");

      if (this.isAnalyticsDataValid(localStorageKey)) {
        processedData = this.getStoredAnalyticsData(localStorageKey);
      } else {
        const analyticsData = await this.getAnalyticsData(this.selectedTimeframeLine, "storyOpened");
        processedData = this.processAnalyticsData(this.selectedTimeframeLine, analyticsData, "storyOpened");
      }

      this.lineChartData[0].data = this.lineChartLabels.map(label => processedData.chartData[label] || 0);
      // Fetch unique users count separately to avoid double-counting
      this.totalLineChartUsers = await this.getUniqueUsersCount(this.selectedTimeframeLine, "storyOpened");
    }
    // Calculate total events
    this.totalLineChartEvents = this.lineChartData[0].data.reduce((sum: number, value) => {
      const numValue = typeof value === 'number' ? value : 0;
      return sum + numValue;
    }, 0);

    this.lineChartData = [...this.lineChartData];
  }

  // Helper methods moved to AnalyticsHelpersService

  // Define chart data and options
  barChartType: ChartType = 'bar';
  lineChartType: ChartType = 'line';

  barChartData: ChartDataset<'bar'>[] = [{ data: [], label: '', backgroundColor: 'rgba(50, 206, 255, 1)', borderColor: 'rgba(50, 206, 255, 1)', borderRadius: 16 }];
  lineChartData: ChartDataset<'line'>[] = [{ data: [], label: '', borderColor: 'rgba(50, 206, 255, 1)', backgroundColor: 'rgba(50, 206, 255, 1)' }];

  barChartLabels: string[] = [];
  lineChartLabels: string[] = [];

  barChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      }
    }
  };
  lineChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      }
    }
  };

  // Update the updateCustomDateRange method
  async updateCustomDateRange(startDate: string, endDate: string, chartType: 'bar' | 'line'): Promise<void> {
    try {
      this.loadingSpinnerService.show(); // Show loader
      this.customStartDate = startDate;
      this.customEndDate = endDate;

      // Format the date range label
      const formattedLabel = this.helpers.formatDateRangeLabel(startDate, endDate);

      if (chartType === 'bar') {
        this.barChartCustomDateRangeLabel = formattedLabel;
        await this.updateBarChart('custom');
      } else if (chartType === 'line') {
        this.lineChartCustomDateRangeLabel = formattedLabel;
        await this.updateLineChart('custom');
      }
    } catch (error) {
      console.error('Error updating custom date range:', error);
    } finally {
      this.loadingSpinnerService.hide(); // Hide loader
    }
  }

  // Formatting handled by helpers service

  initializeDatePickers(): void {
    const flatpickrConfig: any = {
      mode: 'range' as const,
      dateFormat: 'Y-m-d',
      maxDate: 'today',
      closeOnSelect: false,
      appendTo: undefined,

      onReady: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => {
        // Create buttons container
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'flatpickr-buttons';
        buttonContainer.style.padding = '10px';
        buttonContainer.style.textAlign = 'right';
        buttonContainer.style.borderTop = '1px solid #e6e6e6';

        // Create Apply button
        const applyBtn = document.createElement('button');
        applyBtn.innerHTML = 'Apply';
        applyBtn.className = 'flatpickr-apply-btn';
        applyBtn.style.marginRight = '10px';
        applyBtn.style.padding = '8px 16px';
        applyBtn.style.background = '#32ceff';
        applyBtn.style.border = 'none';
        applyBtn.style.borderRadius = '4px';
        applyBtn.style.color = 'white';
        applyBtn.style.cursor = 'pointer';

        // Create Cancel button
        const cancelBtn = document.createElement('button');
        cancelBtn.innerHTML = 'Cancel';
        cancelBtn.className = 'flatpickr-cancel-btn';
        cancelBtn.style.padding = '8px 16px';
        cancelBtn.style.background = '#f0f0f0';
        cancelBtn.style.border = 'none';
        cancelBtn.style.borderRadius = '4px';
        cancelBtn.style.cursor = 'pointer';
        cancelBtn.style.marginRight = '12px';

        // Add buttons to container
        buttonContainer.appendChild(cancelBtn);
        buttonContainer.appendChild(applyBtn);

        // Add container to calendar
        const calendar = instance.calendarContainer;
        calendar.appendChild(buttonContainer);

        // Handle button clicks
        cancelBtn.addEventListener('click', () => {
          // instance.clear();
          instance.close();
        });
      },

      // Update onClose to handle both cancel button and outside clicks
      onClose: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => {
        if (instance.element.id === 'barChartDateRangePicker') {
          // Only revert if we're not in 'custom' mode (meaning Apply wasn't clicked)
          if (this.selectedTimeframeBar !== 'custom') {
            this.selectedTimeframeBar = this.previousTimeframeBar;
          }
        } else if (instance.element.id === 'lineChartDateRangePicker') {
          if (this.selectedTimeframeLine !== 'custom') {
            this.selectedTimeframeLine = this.previousTimeframeLine;
          }
        }
      }
    };

    // Bar chart date picker
    const barChartDateRangeInput = document.querySelector('#barChartDateRangePicker') as HTMLInputElement;
    if (barChartDateRangeInput) {
      this.barChartFlatpickrInstance = flatpickr(barChartDateRangeInput, {
        ...flatpickrConfig,
        defaultDate: [this.barChartCustomStartDate, this.barChartCustomEndDate],
        onReady: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => {
          flatpickrConfig.onReady(selectedDates, dateStr, instance);
          const applyBtn = instance.calendarContainer.querySelector('.flatpickr-apply-btn');
          const cancelBtn = instance.calendarContainer.querySelector('.flatpickr-cancel-btn');

          if (cancelBtn) {
            cancelBtn.addEventListener('click', () => {
              this.selectedTimeframeBar = this.previousTimeframeBar;
            });
          }

          if (applyBtn) {
            applyBtn.addEventListener('click', () => {
              const dates = instance.selectedDates;
              if (dates.length === 2) {
                this.barChartCustomStartDate = this.helpers.formatDate(dates[0]);
                this.barChartCustomEndDate = this.helpers.formatDate(dates[1]);
                this.selectedTimeframeBar = 'custom';  // Update this line
                this.updateCustomDateRange(this.barChartCustomStartDate, this.barChartCustomEndDate, 'bar');
                instance.close();
              }
            });
          }
        }
      });
    }

    // Line chart date picker
    const lineChartDateRangeInput = document.querySelector('#lineChartDateRangePicker') as HTMLInputElement;
    if (lineChartDateRangeInput) {
      this.lineChartFlatpickrInstance = flatpickr(lineChartDateRangeInput, {
        ...flatpickrConfig,
        defaultDate: [this.lineChartCustomStartDate, this.lineChartCustomEndDate],
        onReady: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => {
          flatpickrConfig.onReady(selectedDates, dateStr, instance);
          const applyBtn = instance.calendarContainer.querySelector('.flatpickr-apply-btn');
          const cancelBtn = instance.calendarContainer.querySelector('.flatpickr-cancel-btn');

          if (cancelBtn) {
            cancelBtn.addEventListener('click', () => {
              this.selectedTimeframeLine = this.previousTimeframeLine;
            });
          }

          if (applyBtn) {
            applyBtn.addEventListener('click', () => {
              const dates = instance.selectedDates;
              if (dates.length === 2) {
                this.lineChartCustomStartDate = this.helpers.formatDate(dates[0]);
                this.lineChartCustomEndDate = this.helpers.formatDate(dates[1]);
                this.selectedTimeframeLine = 'custom';  // Update this line
                this.updateCustomDateRange(this.lineChartCustomStartDate, this.lineChartCustomEndDate, 'line');
                instance.close();
              }
            });
          }
        }
      });
    }
  }

  // Date formatting handled by helpers service

  // ================= Playground filter helpers =================
  private async loadManagedPlaygroundIds(): Promise<void> {
    try {
      this.loadingSpinnerService.show();
      
      // Fetch businesses from Firestore using the service
      const businesses = await this.businessesService.getManagedBusinesses();
      
      if (Array.isArray(businesses)) {
        // Normalize to array of objects { id, name }
        this.managedPlaygroundIds = businesses.map((item: any) => {
          if (typeof item === 'string') {
            return { id: item, name: item };
          }
          if (item && typeof item === 'object') {
            const id = item.id ?? item.businessId ?? item.value ?? '';
            const name = item.name ?? item.label ?? id;
            return { id: String(id), name: String(name) };
          }
          return { id: String(item), name: String(item) };
        }).filter((p: { id: string; name: string }) => p.id);
      } else {
        this.managedPlaygroundIds = [];
      }
      
      // Default: All selected
      this.allSelectedPlaygrounds = true;
      this.tempAllSelectedPlaygrounds = true;
      this.selectedPlaygroundIds = new Set<string>(this.managedPlaygroundIds.map(p => p.id));
      this.tempSelectedPlaygroundIds = new Set<string>(this.managedPlaygroundIds.map(p => p.id));
    } catch (error) {
      console.error('Error loading managed playground IDs:', error);
      this.managedPlaygroundIds = [];
    } finally {
      this.loadingSpinnerService.hide();
    }
  }

  togglePlaygroundFilterDropdown(): void {
    this.isPlaygroundFilterOpen = !this.isPlaygroundFilterOpen;
    // Sync temp state with persisted when opening
    if (this.isPlaygroundFilterOpen) {
      this.tempAllSelectedPlaygrounds = this.allSelectedPlaygrounds;
      this.tempSelectedPlaygroundIds = new Set<string>(
        this.allSelectedPlaygrounds ? this.managedPlaygroundIds.map(p => p.id) : Array.from(this.selectedPlaygroundIds)
      );
    }
  }

  onToggleAllPlaygrounds(checked: boolean): void {
    this.tempAllSelectedPlaygrounds = checked;
    if (checked) {
      this.tempSelectedPlaygroundIds = new Set<string>(this.managedPlaygroundIds.map(p => p.id));
    }
  }

  onToggleOnePlayground(id: string, checked: boolean): void {
    if (checked) {
      this.tempSelectedPlaygroundIds.add(id);
    } else {
      this.tempSelectedPlaygroundIds.delete(id);
    }
    // If any item is unselected, uncheck All
    if (this.tempSelectedPlaygroundIds.size !== this.managedPlaygroundIds.length) {
      this.tempAllSelectedPlaygrounds = false;
    } else {
      this.tempAllSelectedPlaygrounds = true;
    }
  }

  async applyPlaygroundSelection(): Promise<void> {
    try {
      this.loadingSpinnerService.show();
      this.allSelectedPlaygrounds = this.tempAllSelectedPlaygrounds || this.tempSelectedPlaygroundIds.size === this.managedPlaygroundIds.length;
      this.selectedPlaygroundIds = new Set<string>(this.tempSelectedPlaygroundIds);
      this.isPlaygroundFilterOpen = false;
      // Refresh all analytics views
      await this.updateBarChart();
      await this.updateLineChart();
      this.notificationsOpenedAnalytics = await this.getAnalyticsData('0', 'notificationOpened');
      const processedData = this.processAnalyticsData('0', this.notificationsOpenedAnalytics, 'notificationOpened');
      this.notificationOpened = processedData.chartData.notificationOpened > 0 ? processedData.chartData.notificationOpened : '0';
      
      // Refresh totalUsers with hardcoded date range (all-time)
      const totalUsersAnalytics = await this.getAnalyticsData('totalUsers', 'storyOpened');
      const totalUsersProcessedData = this.processAnalyticsData('totalUsers', totalUsersAnalytics, 'storyOpened');
      this.totalUsers = totalUsersProcessedData.totalUsers > 0 ? totalUsersProcessedData.totalUsers.toString() : '0';
    } finally {
      this.loadingSpinnerService.hide();
    }
  }

  getPlaygroundSelectionLabel(): string {
    if (this.allSelectedPlaygrounds || this.selectedPlaygroundIds.size === this.managedPlaygroundIds.length) {
      return 'All playgrounds';
    }
    return `${this.selectedPlaygroundIds.size} selected`;
  }

  private getSelectedPlaygroundIdsForQuery(): string[] {
    // If none configured, fallback to current businessId
    if (!this.managedPlaygroundIds.length) {
      return [this.businessId];
    }
    if (this.allSelectedPlaygrounds || this.selectedPlaygroundIds.size === 0) {
      return this.managedPlaygroundIds.map(p => p.id);  
    }
    return Array.from(this.selectedPlaygroundIds);
  }

  private buildBusinessIdFilterExpression(ids: string[]): any {
    const uniqueIds = Array.from(new Set(ids));
    if (uniqueIds.length <= 1) {
      const value = uniqueIds[0] || this.businessId;
      return {
        filter: {
          fieldName: 'customEvent:businessId',
          stringFilter: { matchType: 'EXACT', value }
        }
      };
    }
    return {
      filter: {
        fieldName: 'customEvent:businessId',
        inListFilter: { values: uniqueIds }
      }
    };
  }

  private getSelectionCacheKey(): string {
    const ids = this.getSelectedPlaygroundIdsForQuery().slice().sort();
    return ids.length ? ids.join('-') : this.businessId;
  }

  private getAnalyticsLocalStorageKey(timeRange: string, eventName: string): string {
    const selectionKey = this.getSelectionCacheKey();
    if (timeRange === '0' || timeRange === 'totalUsers') {
      return `analyticsData_${eventName}_${selectionKey}`;
    }
    if (timeRange.startsWith('custom')) {
      return `analyticsData_${timeRange}_${this.customStartDate}_${this.customEndDate}_${eventName}_${selectionKey}`;
    }
    return `analyticsData_${timeRange}_${eventName}_${selectionKey}`;
  }

  ngOnDestroy(): void {
    if (this.barChartFlatpickrInstance) {
      this.barChartFlatpickrInstance.destroy();
    }
    if (this.lineChartFlatpickrInstance) {
      this.lineChartFlatpickrInstance.destroy();
    }
  }
}














































































